import { some, compact, isEqual } from 'lodash'
import { listAllControllers } from '@wix/wix-data-client-common'
import { getRepeaterItemAncestor } from '../../business-logic/components/getRepeaterItemAncestor'
import type { ComponentRef, Connection } from '@wix/editor-platform-sdk-types'
import type { EditorSDK } from '@wix/wix-data-client-common-standalone'
import type { RepeaterAncestor } from '../../worker/data-binding-api/utils'

type NonNullableRepeaterAncestor = NonNullable<RepeaterAncestor>

// optimize this function to be executed only once
// since the outcome shouldn't change while panel is open
const areAnyOtherRepeaterChildrenConnected = async function ({
  editorSdkProxy,
  repeaterRef,
  componentRef,
}: {
  editorSdkProxy: EditorSDK
  repeaterRef: ComponentRef
  componentRef: ComponentRef
}) {
  const [maybeRepeaterItemContainer, allDatasets] = await Promise.all([
    getRepeaterItemAncestor({
      editorSdkProxy,
      repeaterRef,
      componentRef,
    }),
    listAllControllers(editorSdkProxy),
  ])

  const repeaterItemContainer =
    maybeRepeaterItemContainer.getOrElse(componentRef)

  const repeaterItemChildren = await editorSdkProxy.components.getChildren({
    componentRef: repeaterItemContainer,
    recursive: true,
  })

  const repeaterChildrenExcludingComponent = compact([
    repeaterItemContainer,
    ...repeaterItemChildren,
  ]).filter(childComponentRef => !isEqual(childComponentRef, componentRef))
  const repeaterOtherChildrenProperties = await editorSdkProxy.components.get({
    componentRefs: repeaterChildrenExcludingComponent,
    properties: ['connections'],
  })

  function isDatasetConnection(connection: Connection) {
    return some(allDatasets, { controllerRef: connection.controllerRef })
  }

  for (const component of repeaterOtherChildrenProperties) {
    for (const connection of component.connections) {
      if (isDatasetConnection(connection)) {
        return true
      }
    }
  }

  return false
}

const isRepeaterAncestorConnected = (
  repeaterAncestor: NonNullableRepeaterAncestor,
) => repeaterAncestor.controllerRef !== null

export const shouldConnectionChangeAffectRepeater = async ({
  editorSdkProxy,
  componentRef,
  repeaterAncestor,
  connecting,
}: {
  editorSdkProxy: EditorSDK
  componentRef: ComponentRef
  repeaterAncestor: NonNullableRepeaterAncestor
  connecting: boolean
}) =>
  isRepeaterAncestorConnected(repeaterAncestor) !== connecting &&
  !(await areAnyOtherRepeaterChildrenConnected({
    editorSdkProxy,
    repeaterRef: repeaterAncestor.componentRef,
    componentRef,
  }))
